import React from 'react';
import Select from 'react-select';
import { Pill } from './Pill';

// Shared by the QuickBooks area's Customers tab and the Customer page (Link
// Customers to QuickBooks Customers): picking a QuickBooks Customer, what
// needs attention on a link, and how QuickBooks differs from Optim before
// linking overwrites it.

const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    fontSize: 14,
    borderColor: state.isFocused ? '#1976d2' : '#bfc8d6',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.15)' : 'none',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  indicatorsContainer: (base) => ({ ...base, height: 30 }),
  option: (base) => ({ ...base, fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

export function customerLabel(customer) {
  if (!customer) return '';
  return customer.email ? `${customer.name} · ${customer.email}` : customer.name;
}

// Active, top-level QuickBooks Customers not yet linked to another Optim
// Customer, searchable by name or email.
export function QuickBooksCustomerPicker({ customers, value, onChange, disabled, autoFocus, placeholder = 'Search QuickBooks Customers...' }) {
  const options = customers.map((customer) => ({ value: customer.id, label: customerLabel(customer), customer }));
  return (
    <Select
      value={options.find((opt) => opt.value === value) || null}
      onChange={(opt) => onChange(opt ? opt.customer : null)}
      options={options}
      isDisabled={disabled}
      isSearchable
      autoFocus={autoFocus}
      placeholder={placeholder}
      noOptionsMessage={() => 'No unlinked QuickBooks Customers match'}
      styles={SELECT_STYLES}
      menuPortalTarget={document.body}
    />
  );
}

// One warning pill per thing needing attention on a link; nothing for a
// healthy one. Hover shows the reason where there is one.
export function QuickBooksIssuePills({ issues }) {
  if (!issues?.length) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {issues.map((issue) => (
        <Pill key={issue.code} color="#b91c1c" background="#fef2f2" title={issue.message || issue.label}>{issue.label}</Pill>
      ))}
    </span>
  );
}

const CELL = { padding: '4px 8px', fontSize: 13, verticalAlign: 'top', borderBottom: '1px solid #eef0f3' };

// Optim beside QuickBooks for each field that differs; linking overwrites
// the QuickBooks side.
export function QuickBooksDifferences({ differences }) {
  if (!differences) return null;
  if (!differences.length) return <div style={{ fontSize: 13, color: '#1a7f4b' }}>QuickBooks already matches Optim.</div>;
  return (
    <div>
      <div style={{ fontSize: 13, color: '#92400e', marginBottom: 6 }}>Linking overwrites QuickBooks with Optim’s details:</div>
      <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 720 }}>
        <thead>
          <tr>
            <th style={{ ...CELL, textAlign: 'left', fontWeight: 600, width: '18%' }}></th>
            <th style={{ ...CELL, textAlign: 'left', fontWeight: 600 }}>Optim (kept)</th>
            <th style={{ ...CELL, textAlign: 'left', fontWeight: 600 }}>QuickBooks now</th>
          </tr>
        </thead>
        <tbody>
          {differences.map((d) => (
            <tr key={d.field}>
              <td style={{ ...CELL, fontWeight: 600 }}>{d.field}</td>
              <td style={CELL}>{d.optim || <span style={{ color: '#888' }}>blank</span>}</td>
              <td style={{ ...CELL, color: '#666', textDecoration: 'line-through' }}>{d.qbo || <span style={{ color: '#888', textDecoration: 'none' }}>blank</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
